import { startTransition, useEffect, useMemo } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, IActionFormProps, TableAdapter } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Field, Label, Switch } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { isEmpty } from '@/shared/utils'

import { ActionWorkflowColumnRepeater } from '../../action-workflows/ActionWorkflowColumnRepeater'
import ActFormWithInputOutput from '../../action-workflows/simulations/ActFormWithInputOutput'
import { TableSelect } from '../../shared/TableSelect'

export default function ActFormFilter({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { tables, dbClient, dbWorker, language, loadedTables } = useDbClientContext()

  // create table options
  const tblOptions = useMemo(() => {
    return [{ id: '', name: 'Select Table' } as TableAdapter, ...tables.filter((tb) => !tb.isSnapshot)]
  }, [tables])

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      tableId: initialData?.tableId || undefined,
      conditions: initialData?.conditions || {
        isAny: true,
        conditions: [],
      },
      returnFirstOnly: initialData?.returnFirstOnly ?? true,
    },
    validate(values, setError) {
      if (!values.tableId || !values.tableId?.trim()?.length) {
        setError('tableId', 'Please choose a table')
      }
    },
  })

  const handleSubmit = onFormSubmit((data: any) => {
    onSubmit(data, { preventClose: true })
  })

  // change handler for table selection
  const handleChangeTable = (tb: TableAdapter) => {
    startTransition(() => {
      form.setValues('tableId', tb.id)
      form.setValues('conditions', {
        isAny: form.getValues('conditions')?.isAny ?? true,
        conditions: [],
      })
    })
  }

  const handleConditionsChange = (conditions: any) => {
    form.setValues('conditions', conditions)
  }

  const currentConditions = form.getValues('conditions')
  const currentTableId = form.getValues('tableId')

  // Fetch table data when table changes
  useEffect(() => {
    if (!dbClient || !dbWorker || !currentTableId) return

    // Check if the selected table is already loaded
    if (loadedTables.has(currentTableId)) return

    // Fetch the table data using dbWorker
    dbWorker.sendAction({
      type: 'get_table_data',
      payload: { table_id: currentTableId, language: language },
    })
  }, [dbClient, dbWorker, currentTableId, language, loadedTables])

  // Get the selected table object and create filtered column options
  const { selectedTable, columnOptions } = useMemo(() => {
    const table = tables.find((tb) => tb.id === currentTableId)
    if (!table) {
      return { selectedTable: null, columnOptions: [] }
    }

    // Filter out unsupported column types (similar to ColumnFilterFieldSetting)
    const tableColumns = (table.columns ?? []).filter(
      (c) =>
        ![
          ColumnTypes.Connection,
          ColumnTypes.RichText,
          ColumnTypes.RowID,
          ColumnTypes.Address,
          ColumnTypes.Slug,
          ColumnTypes.Array,
          ColumnTypes.QRCode,
          ColumnTypes.ImageArray,
          ColumnTypes.Barcode,
          ColumnTypes.AISEO,
          ColumnTypes.AITextProcessing,
        ].includes(c.type)
    )

    const colOptions = [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...tableColumns]

    return { selectedTable: table, columnOptions: colOptions }
  }, [tables, currentTableId])

  const invalidForm =
    !currentTableId ||
    isEmpty(currentConditions?.conditions) ||
    currentConditions?.conditions?.length === 0 ||
    !!form.errors

  return (
    <ActFormWithInputOutput
      data={{
        chosenTableId: currentTableId,
        conditions: currentConditions,
        returnFirstOnly: form.getValues('returnFirstOnly'),
      }}
    >
      <div className='flex flex-col h-full md:min-w-[678px]'>
        {/* Header */}
        {/* <div className='border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between'>
          <div>
            <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
              <Trans>Filter</Trans>
            </h3>
            <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
              <Trans>Only items that match your conditions will pass through to the next step</Trans>
            </p>
          </div>
        </div> */}

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='space-y-6'>
            {/* Info Section */}
            <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4'>
              <div className='flex items-start gap-3'>
                {/* <MaterialIcon icon='filter_alt' size={20} className='text-blue-600 dark:text-blue-400 mt-0.5' /> */}
                <div>
                  <h4 className='text-sm font-medium text-blue-900 dark:text-blue-100 mb-1'>
                    <Trans>How filtering works</Trans>
                  </h4>
                  <p className='text-sm text-blue-700 dark:text-blue-300'>
                    <Trans>
                      Select a table source and define filter conditions to keep only the data rows that match your
                      criteria. Rows that don&apos;t meet the conditions will be excluded from the results.
                    </Trans>
                  </p>
                </div>
              </div>
            </div>

            {/* Table Selection */}
            <div className='border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
              <Field className='flex flex-col gap-2' isVertical>
                <Label required className='w-full whitespace-nowrap'>
                  <Trans>Select Table to Filter</Trans>
                </Label>
                <TableSelect
                  options={tblOptions}
                  value={tblOptions.find((tb) => tb.id === currentTableId)}
                  onChange={handleChangeTable}
                  error={form.errors?.tableId}
                />
              </Field>
            </div>

            {/* Filter Conditions Section - Only show when table is selected */}
            {currentTableId && selectedTable && (
              <div className='border border-gray-200 dark:border-gray-700 rounded-lg'>
                <div className='p-4 border-b border-gray-200 dark:border-gray-700'>
                  <div className='flex items-center gap-3'>
                    <MaterialIcon icon='tune' size={24} className='text-gray-700 dark:text-gray-300' />
                    <div>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                        <Trans>Filter Conditions</Trans>
                      </h3>
                      <p className='text-sm text-gray-600 dark:text-gray-400'>
                        <Trans>Define which items should pass through the filter</Trans>
                      </p>
                    </div>
                  </div>
                </div>

                <div className='p-4 space-y-6'>
                  <div className='space-y-4'>
                    <div className='space-y-6'>
                      <ActionWorkflowColumnRepeater
                        id='filter-conditions'
                        value={currentConditions}
                        onChange={handleConditionsChange}
                        colOptions={columnOptions}
                        tables={tables}
                        table={selectedTable}
                        actionOptions={initialData?.actionOptions}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Return only first matching row toggle */}
          <div className='pt-4 border-t border-gray-200 dark:border-gray-700'>
            <Field className='flex items-center justify-start' isVertical={false}>
              <div>
                <Switch
                  id='filter-return-first-only'
                  checked={form.getValues('returnFirstOnly')}
                  disabled
                  onClick={(isChecked) => form.setValues('returnFirstOnly', isChecked)}
                />
              </div>
              <Label className='text-sm font-medium text-gray-900 dark:text-white mb-0 whitespace-nowrap'>
                <Trans>Return only first matching row</Trans>
              </Label>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <div className='border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center bg-gray-50 dark:bg-transparent'>
          <Button
            variant={ButtonVariant.Outlined}
            onClick={onDismiss}
            className='text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-gray-300 dark:border-gray-600'
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleSubmit} disabled={invalidForm}>
            <Trans>Apply Changes</Trans>
          </Button>
        </div>
      </div>
    </ActFormWithInputOutput>
  )
}
